var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useEffect, useState, forwardRef, useImperativeHandle, } from "react";
import { Grid } from "@mui/material";
import { useForm } from "react-hook-form";
import { Icon } from "@iconify/react";
import { useTranslation } from "react-i18next";
// Components
import { FInput } from "@ui/components/inputs/FInput";
import { SectionWithInfo } from "@ui/components/section/SectionWithInfo";
import { useAppDispatch } from "@application/store/useStore";
import { updateAccessPointConfiguration } from "@reducers/access-points/thunk/access-point-thunk";
import { toast } from "@ui/utils/toaster";
import { userService } from "@utility/user-service";
import { tooltipTextsApi, TOOLTIP_KEYS } from "@api/enpoints/tooltip-texts-api";
const ServerSettings = forwardRef(({ control, errors, validationRules, isEdit, ap }, ref) => {
    const dispatch = useAppDispatch();
    const { t } = useTranslation(["common"]);
    const isSuperAdmin = userService.hasRole("Super Admin");
    const [serverSettingsTooltip, setServerSettingsTooltip] = useState("");
    useEffect(() => {
        const fetchTooltips = () => __awaiter(void 0, void 0, void 0, function* () {
            var _a, _b;
            try {
                const res = yield tooltipTextsApi.getTooltipTexts();
                if (((_a = res.data) === null || _a === void 0 ? void 0 : _a.isSuccess) && ((_b = res.data) === null || _b === void 0 ? void 0 : _b.data)) {
                    const d = res.data.data;
                    if (d[TOOLTIP_KEYS.serverSettings] != null)
                        setServerSettingsTooltip(d[TOOLTIP_KEYS.serverSettings]);
                }
            }
            catch (_c) {
                // Non-blocking
            }
        });
        fetchTooltips();
    }, []);
    const handleTooltipSave = (key, value) => __awaiter(void 0, void 0, void 0, function* () {
        var _a, _b, _c, _d, _e, _f, _g;
        try {
            const res = yield tooltipTextsApi.saveTooltipText({ key, text: value });
            if ((_a = res.data) === null || _a === void 0 ? void 0 : _a.isSuccess) {
                toast.success((_b = t("common:toasts.saved")) !== null && _b !== void 0 ? _b : "Saved");
            }
            else {
                toast.error((_d = (_c = res.data) === null || _c === void 0 ? void 0 : _c.message) !== null && _d !== void 0 ? _d : t("common:toasts.failedToSaveTooltip"));
            }
        }
        catch (err) {
            toast.error((_g = (_f = (_e = err === null || err === void 0 ? void 0 : err.response) === null || _e === void 0 ? void 0 : _e.data) === null || _f === void 0 ? void 0 : _f.message) !== null && _g !== void 0 ? _g : t("common:toasts.failedToSaveTooltip"));
        }
    });
    const { control: serverControl, handleSubmit, reset, formState: { errors: serverErrors }, } = useForm({
        defaultValues: {
            hostName: "",
            url: "",
            ntpServer: "",
            ntpSyncInterval: "",
            noNodeCommunicationTimeoutMinutes: "",
            noApCommunicationDelayMinutes: "",
        },
    });
    useImperativeHandle(ref, () => ({
        submitForm: () => {
            handleSubmit(handleSaveServerSettings)();
        },
    }));
    useEffect(() => {
        if (ap) {
            const config = ap.configuration;
            const alarmSettings = ap.alarmSettings;
            reset({
                hostName: (config === null || config === void 0 ? void 0 : config.hostName) || "",
                url: (config === null || config === void 0 ? void 0 : config.url) || "",
                ntpServer: (config === null || config === void 0 ? void 0 : config.ntpServer) || "",
                ntpSyncInterval: String(config === null || config === void 0 ? void 0 : config.ntpSyncInterval),
                noNodeCommunicationTimeoutMinutes: (alarmSettings === null || alarmSettings === void 0 ? void 0 : alarmSettings.noNodeCommunicationTimeoutMinutes) != null
                    ? String(alarmSettings.noNodeCommunicationTimeoutMinutes)
                    : "",
                noApCommunicationDelayMinutes: (alarmSettings === null || alarmSettings === void 0 ? void 0 : alarmSettings.noApCommunicationDelayMinutes) != null
                    ? String(alarmSettings.noApCommunicationDelayMinutes)
                    : "",
            });
        }
    }, [ap, reset]);
    const handleSaveServerSettings = (data) => __awaiter(void 0, void 0, void 0, function* () {
        if (!(ap === null || ap === void 0 ? void 0 : ap.accessPointUID)) {
            toast.error(t("common:toasts.accessPointUidRequired"));
            return;
        }
        try {
            yield dispatch(updateAccessPointConfiguration({
                accessPointUid: ap.accessPointUID,
                data: {
                    accessPointUID: ap.accessPointUID,
                    configuration: {
                        hostName: data.hostName,
                        url: data.url,
                        ntpServer: data.ntpServer,
                        ntpSyncInterval: parseInt(data.ntpSyncInterval) || 86400,
                    },
                    alarmSettings: {
                        noNodeCommunicationTimeoutMinutes: data.noNodeCommunicationTimeoutMinutes
                            ? parseInt(data.noNodeCommunicationTimeoutMinutes, 10) ||
                                undefined
                            : undefined,
                        noApCommunicationDelayMinutes: data.noApCommunicationDelayMinutes
                            ? parseInt(data.noApCommunicationDelayMinutes, 10) ||
                                undefined
                            : undefined,
                    },
                    isGeneralSettings: false,
                    isAlarmSettings: true,
                },
            })).unwrap();
            toast.success(t("common:toasts.configurationUpdatedSuccessfully"));
        }
        catch (error) {
            toast.error(error || t("common:toasts.failedToUpdateConfiguration"));
        }
    });
    if (!isSuperAdmin) {
        return null;
    }
    return (React.createElement(Grid, { container: true, spacing: 2 },
        React.createElement(Grid, { size: { xs: 12 } },
            React.createElement(SectionWithInfo, { title: t("common:headings.serverSettings"), tooltipText: serverSettingsTooltip, onTooltipSave: (value) => {
                    setServerSettingsTooltip(value);
                    handleTooltipSave(TOOLTIP_KEYS.serverSettings, value);
                } },
                React.createElement(Grid, { container: true, spacing: 2 },
                    React.createElement(Grid, { size: { xs: 12, md: 6 } },
                        React.createElement(FInput, { name: "hostName", control: serverControl, endIcon: React.createElement(Icon, { icon: "solar:cloud-linear" }), label: t("common:labels.serverAddress"), placeholder: "beta.sitexinsight.nl" })),
                    React.createElement(Grid, { size: { xs: 12, md: 6 } },
                        React.createElement(FInput, { name: "url", control: serverControl, endIcon: React.createElement(Icon, { icon: "material-symbols:code-rounded" }), label: t("common:labels.serverPath"), placeholder: "/ap" })),
                    React.createElement(Grid, { size: { xs: 12, md: 6 } },
                        React.createElement(FInput, { name: "ntpServer", control: serverControl, label: t("common:labels.ntpServer"), endIcon: React.createElement(Icon, { icon: "mdi-light:clock" }), placeholder: "nl.pool.ntp.org" })),
                    React.createElement(Grid, { size: { xs: 12, md: 6 } },
                        React.createElement(FInput, { name: "ntpSyncInterval", control: serverControl, endIcon: React.createElement(Icon, { icon: "mdi-light:clock" }), label: t("common:labels.ntpInterval"), placeholder: "86400" })),
                    React.createElement(Grid, { size: { xs: 12, md: 6 } },
                        React.createElement(FInput, { name: "noNodeCommunicationTimeoutMinutes", control: serverControl, label: t("common:labels.timeoutNodeOffline"), placeholder: "30" })),
                    React.createElement(Grid, { size: { xs: 12, md: 6 } },
                        React.createElement(FInput, { name: "noApCommunicationDelayMinutes", control: serverControl, label: t("common:labels.timeoutApOffline"), placeholder: "30" })))))));
});
export default ServerSettings;
